// Import all store modules
// Pinia instance must be imported first to avoid TDZ in circular dependencies
import { pinia } from './piniaInstance'
import { preloadedStores, getLoadedStore } from './storeRegistry'
import { useGameVersionStore } from './gameVersionStore'
import { useCampaignStore } from './campaignStore'
import { useUserStore } from './userStore'
import { useCharacterStore } from './characterStore'
import { isCacheValid, createCacheInfo } from './cacheUtils'
// Avoid top-level import of global cache helpers to prevent TDZ/cycles in prod builds

// Pinia instance exported from separate module to prevent circular imports
export { pinia }

// Function to initialize pinia helpers (called by boot)
export async function initializePiniaHelpers() {
  // Only initialize once
  if (pinia._helpersInitialized) return
  pinia._helpersInitialized = true

  // Add the custom use method to the Pinia instance
  pinia.use = (callback) => {
    if (typeof callback === 'function') {
      // Create a store object with all our store functions
      const store = {
        useRaceStore,
        useClassStore,
        useBackgroundStore,
        useSpellStore,
        useCharacterStore,
        useSkillStore,
        useFeatureStore,
        useMonsterStore,
        useWeaponStore,
        useGameVersionStore,
        useEquipmentStore,
        useSubraceStore,
        useAbilityStore,
        useConditionStore,
        useCampaignStore,
        useUserStore
      }

      // Call the callback with an object that has a store property
      callback({ store })
    }
    return pinia
  }

  // Add helper utilities for memory management to the pinia instance
  pinia.clearStoreCache = (versionId) => {
    if (versionId) {
      clearVersionStores(versionId)
      return { cleared: true, versionId }
    } else {
      // Clear all
      Object.keys(storeCache).forEach((key) => delete storeCache[key])
      versionAccessOrder.length = 0
      return { cleared: true, allVersions: true }
    }
  }

  // Add global cache clearing helpers (lazy-resolved to avoid TDZ/cycles)
  pinia.clearAllCaches = async (options = {}) => {
    const mod = await import('../utils/globalCacheManager')
    return mod.clearAllCaches(options)
  }
  pinia.clearGameVersionCaches = async (gameVersion) => {
    const mod = await import('../utils/globalCacheManager')
    return mod.clearGameVersionCaches(gameVersion)
  }
}

// Cache for loaded store modules
const storeCache = {}

// Maximum number of version-specific stores to keep in memory
const MAX_CACHED_VERSIONS = 3

// Track the order of version use to implement LRU cache strategy
const versionAccessOrder = []

// Dynamic version-specific stores loader
async function getVersionSpecificStore(storeName) {
  const gameVersionStore = useGameVersionStore()
  const versionId = gameVersionStore.effectiveVersionId || 'dnd_5e'

  // Update the version access order list for LRU cache tracking
  updateVersionAccessOrder(versionId)

  // Check if we've already loaded this store
  const cacheKey = `${versionId}/${storeName}`
  if (storeCache[cacheKey]) {
    return storeCache[cacheKey]
  }

  try {
    // Try to import from version-specific folder using dynamic import
    const storeModule = await import(`./versions/${versionId}/${storeName}Store.js`)
    const storeHook =
      storeModule[`use${storeName.charAt(0).toUpperCase() + storeName.slice(1)}Store`]

    // Store in cache with timestamp for potential future cleanup
    storeCache[cacheKey] = storeHook

    // Prune cache if it gets too large
    pruneStoreCache()

    return storeHook
  } catch (error) {
    console.warn(
      `Store not found in version folder: ${versionId}/${storeName}, using default 5e version. Error: ${error.message}`
    )
    try {
      // Fallback to the default dnd_5e version using dynamic import
      const storeModule = await import(`./versions/dnd_5e/${storeName}Store.js`)
      const storeHook =
        storeModule[`use${storeName.charAt(0).toUpperCase() + storeName.slice(1)}Store`]
      storeCache[cacheKey] = storeHook
      return storeHook
    } catch (fallbackError) {
      console.error(`Failed to load store from version folder: ${fallbackError}`)
      // If that fails too, throw error to prevent silent failures
      throw new Error(`Could not load store for ${storeName} from any version folder`)
    }
  }
}

// Keep track of versions in order of use for LRU cache
function updateVersionAccessOrder(versionId) {
  // Remove if already exists
  const index = versionAccessOrder.indexOf(versionId)
  if (index > -1) {
    versionAccessOrder.splice(index, 1)
  }

  // Add to the end (most recently used)
  versionAccessOrder.push(versionId)
}

// Prune the cache when it exceeds maximum size using LRU strategy
function pruneStoreCache() {
  const uniqueVersions = [...new Set(versionAccessOrder)]

  // If we have more versions than allowed, remove the least recently used ones
  if (uniqueVersions.length > MAX_CACHED_VERSIONS) {
    // Get versions to remove (from start of array = least recently used)
    const versionsToRemove = uniqueVersions.slice(0, uniqueVersions.length - MAX_CACHED_VERSIONS)

    // Remove stores for these versions from cache
    for (const version of versionsToRemove) {
      const keysToRemove = Object.keys(storeCache).filter((key) => key.startsWith(`${version}/`))
      keysToRemove.forEach((key) => {
        delete storeCache[key]
      })

      // Also remove from access order
      const accessIndex = versionAccessOrder.indexOf(version)
      if (accessIndex > -1) {
        versionAccessOrder.splice(accessIndex, 1)
      }

      console.log(`Pruned store cache for version: ${version}`)
    }
  }
}

// Clear all stores for a specific version
function clearVersionStores(versionId) {
  if (!versionId) return

  // Remove all stores for this version from cache
  const keysToRemove = Object.keys(storeCache).filter((key) => key.startsWith(`${versionId}/`))
  keysToRemove.forEach((key) => {
    delete storeCache[key]
  })

  // Also clear from access order
  const indicesToRemove = []
  versionAccessOrder.forEach((version, index) => {
    if (version === versionId) {
      indicesToRemove.push(index)
    }
  })

  // Remove from end to start to avoid index shifting issues
  for (let i = indicesToRemove.length - 1; i >= 0; i--) {
    versionAccessOrder.splice(indicesToRemove[i], 1)
  }
}

// Create async loader functions that handle the dynamic import
const createAsyncStoreLoader = (storeName) => {
  return async () => {
    if (!preloadedStores[storeName]) {
      // getVersionSpecificStore may return either a hook function or a store instance
      const resolved = await getVersionSpecificStore(storeName)

      if (typeof resolved === 'function') {
        // it's a hook that when called returns the store instance
        preloadedStores[storeName] = resolved
      } else if (resolved && typeof resolved === 'object') {
        // it's already a store instance; wrap it in a function for uniform access
        console.warn(
          `Version-specific store loader for ${storeName} returned a store instance instead of a hook; wrapping it for compatibility.`
        )
        preloadedStores[storeName] = () => resolved
      } else {
        throw new Error(`Invalid store loader result for ${storeName}: ${String(resolved)}`)
      }
    }

    // At this point preloadedStores[storeName] is guaranteed to be a function
    return preloadedStores[storeName]()
  }
}

// Version-specific stores with dynamic imports
const useRaceStore = createAsyncStoreLoader('race')
const useClassStore = createAsyncStoreLoader('class')
const useBackgroundStore = createAsyncStoreLoader('background')
const useSpellStore = createAsyncStoreLoader('spell')
const useSkillStore = createAsyncStoreLoader('skill')
const useFeatureStore = createAsyncStoreLoader('feature')
const useMonsterStore = createAsyncStoreLoader('monster')
const useWeaponStore = createAsyncStoreLoader('weapon')
const useEquipmentStore = createAsyncStoreLoader('equipment')
const useSubraceStore = createAsyncStoreLoader('subrace')
const useAbilityStore = createAsyncStoreLoader('ability')
const useConditionStore = createAsyncStoreLoader('condition')
const useXpStore = createAsyncStoreLoader('xp')

// Export a dummy plugin to satisfy Quasar's expectation
// Real Pinia installation is handled in src/boot/pinia.js to avoid circular dependency TDZ
const dummyStorePlugin = {
  install: () => {},
  use: () => {} // satisfy store.use() check if Quasar/plugins try to access it
}
export default dummyStorePlugin

// Synchronous accessor for already-loaded stores. Returns the store instance
// if the store hook has been preloaded; otherwise null. This allows legacy
// synchronous code to use a store when it's already been initialized by the
// app, without forcing an async import.
// (Moved to storeRegistry.js)

// Export individual stores for direct imports
export {
  useRaceStore,
  useClassStore,
  useBackgroundStore,
  useSpellStore,
  useCharacterStore,
  useSkillStore,
  useFeatureStore,
  useMonsterStore,
  useWeaponStore,
  useGameVersionStore,
  useEquipmentStore,
  useSubraceStore,
  useAbilityStore,
  useConditionStore,
  useXpStore,
  useCampaignStore,
  useUserStore,
  isCacheValid,
  createCacheInfo,
  getLoadedStore
}
